import React, {useContext, useEffect, useState} from 'react';
import {Button, Grid} from '@mui/material';
import {Form, Formik} from 'formik';
import Box from '@mui/material/Box';
import {SNACKBAR_OPEN} from '../../../reducers';
import {DispatchContext} from '../../../store';
import Typography from '@mui/material/Typography';
import CatalogueEntryOne from './CatalogueEntryOne';
import CatalogueEntryTwo from './CatalogueEntryTwo';
import CatalogueEntryThree from './CatalogueEntryThree';
import CatalogueAttachments from './CatalogueAttachments';
import {catalogueInitialValue, catalogueValidation} from './CatalogueInitialModel';
import {catalogueAction, catalogueApi, catalogueTransition} from '../../../services/CatalogueServices';
import {useLocation, useNavigate} from 'react-router-dom';
import {mastersApi} from '../../../services/MasterDataServices';
import PageWrapper from '../../../common/PageWrapper/PageWrapper';

export default function CreateArtefacts() {
  const dispatch = useContext(DispatchContext);
  const user = JSON.parse(localStorage.getItem('user'));
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isEdit = params.get('isEdit');
  const id = params.get('id');
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState(catalogueInitialValue);
  const [originalState, setOriginalState] = useState(null);
  const [code, setCode] = useState('en');
  const [artefactImgIds, setArtefactImgIds] = useState([]);
  const [masters, setMasters] = useState({
    ownerships: [],
    accession_nos: [],
    object_groups: [],
    object_sub_groups: [],
    acquisitions: [],
    materials: [],
    conditions: [],
    place_of_origins: [],
    csr_racks: [],
    dimensions: [],
    sources: [],
  });

  useEffect(() => {
    Promise.all([
      mastersApi('ownerships', 'get'),
      mastersApi('accession_nos', 'get'),
      mastersApi('object_groups', 'get'),
      mastersApi('object_sub_groups', 'get'),
      mastersApi('acquisitions', 'get'),
      mastersApi('materials', 'get'),
      mastersApi('conditions', 'get'),
      mastersApi('place_of_origins', 'get'),
      mastersApi('csr_racks', 'get'),
      mastersApi('dimensions', 'get'),
      mastersApi('sources', 'get'),
    ]).then(([
      ownerships,
      accessionNos,
      objectGroups,
      objectSubGroups,
      acquisitions,
      materials,
      conditions,
      placeOfOrigins,
      csrRacks,
      dimensions,
      sources,
    ]) => {
      setMasters({
        ownerships: ownerships?.data || ownerships || [],
        accession_nos: accessionNos?.data || accessionNos || [],
        object_groups: objectGroups?.data || objectGroups || [],
        object_sub_groups: objectSubGroups?.data || objectSubGroups || [],
        acquisitions: acquisitions?.data || acquisitions || [],
        materials: materials?.data || materials || [],
        conditions: conditions?.data || conditions || [],
        place_of_origins: placeOfOrigins?.data || placeOfOrigins || [],
        csr_racks: csrRacks?.data || csrRacks || [],
        dimensions: dimensions?.data || dimensions || [],
        sources: sources?.data || sources || [],
      });
    });
  }, []);

  const asId = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'object') return value?.id ?? null;
    return value;
  };

  useEffect(() => {
    if (id) {
      catalogueAction('get', id).then(res => {
        // Check if the catalogue status is 'rejected' - if so, prevent editing
        if (res?.data?.state === 'rejected') {
          dispatch({
            type: SNACKBAR_OPEN,
            payload: { 
              isNotify: true, 
              severity: 'error', 
              message: 'Rejected catalogues cannot be edited. Only catalogues rejected for changes can be edited.' 
            }
          });
          navigate('/management/catalogue-artefacts');
          return;
        }
        
        // Remember the state this catalogue was in when the form was opened. If it was
        // 'approved', submitting the form needs to fire the 'initiate_edit' transition
        // first (deferred to submit time so merely opening the form has no side effects).
        setOriginalState(res?.data?.state);

        // Map API dimensions to form dimension_values structure
        const dimensionValues = {};
        (res?.data?.dimensions || []).forEach((dim) => {
          const dimName = dim?.name?.toLowerCase();
          if (dimName === 'length') dimensionValues.length = dim?.value || '0';
          if (dimName === 'width') dimensionValues.width = dim?.value || '0';
          if (dimName === 'height') dimensionValues.height = dim?.value || '0';
          if (dimName === 'weight') dimensionValues.weight = dim?.value || '0';
        });
        
        setInitialValues({
          ...res?.data,
          museum_id: res?.data?.museum_id,
          accession_no: asId(res?.data?.accession_no),
          date_of_aquisition: res?.data?.date_of_aquisition,
          date_of_entry: res?.data?.date_of_entry,
          dimension_ids: [], // Not used anymore but keep for compatibility
          dimension_values: dimensionValues,
          attachment_ids: [],
          translations: {
            en: {
              accession_no: asId(res?.data?.accession_no),
              ownership: asId(res?.data?.ownership),
              classification: asId(res?.data?.object_group || res?.data?.classification),
              // object_type: res?.data?.object_type, // Removed as requested
              object_name: res?.data?.object_name,
              sub_group: asId(res?.data?.object_sub_group || res?.data?.sub_group),
              acquisition: asId(res?.data?.acquisition),
              material: asId(res?.data?.material),
              dyes:  res?.data?.dyes,
              motifs:  res?.data?.motifs,
              description:  res?.data?.description,
              condition: asId(res?.data?.condition),
              location: asId(res?.data?.storage_location) || null,
              place_of_origin: asId(res?.data?.place_of_origin),
              source: asId(res?.data?.source),
              source_remarks: res?.data?.source_remarks || '',
              price: res?.data?.price || '',
              provenance: res?.data?.provenance || '',
              exhibition_history:  res?.data?.exhibition_history,
              other_info:  res?.data?.other_info,
            }
          }
        });
      });
    }
  }, [id]);

  const onSubmit = (values, {setSubmitting}) => {
    const combinedAttachments = (values?.attachments || []).concat(artefactImgIds ? artefactImgIds : []);

    const uniqueIdsSet = new Set();

    combinedAttachments.forEach(id => {
      const parsedId = parseInt(id);
      const parsedIdFromObj = parseInt(id?.id);

      if (!isNaN(parsedId)) {
        uniqueIdsSet.add(parsedId);
      }

      if (!isNaN(parsedIdFromObj)) {
        uniqueIdsSet.add(parsedIdFromObj);
      }
    });

    const newAttachmentIds = Array.from(uniqueIdsSet);

    const artefactsValues = {
      museum_id: user?.museums?.[0]?.id,
      date_of_aquisition: values?.date_of_aquisition,
      date_of_entry: values?.date_of_entry,
      attachment_ids: newAttachmentIds,
      accession_no : values?.translations?.en?.accession_no,
      ownership_id: values?.translations?.en?.ownership,
      object_group_id: values?.translations?.en?.classification,
      object_sub_group_id: values?.translations?.en?.sub_group,
      acquisition_id: values?.translations?.en?.acquisition,
      material_id: values?.translations?.en?.material,
      condition_id: values?.translations?.en?.condition,
      storage_location_id: values?.translations?.en?.location,
      place_of_origin_id: values?.translations?.en?.place_of_origin,
      source_id: values?.translations?.en?.source,
      source_remarks: values?.translations?.en?.source_remarks,
      price: values?.translations?.en?.price,
      dimensions: [
        {
          translations: {
            en: {
              name: "Length",
              description: "This is the cm"
            }
          },
          value: values?.dimension_values?.length || '0'
        },
        {
          translations: {
            en: {
              name: "Width", 
              description: "This is the cm"
            }
          },
          value: values?.dimension_values?.width || '0'
        },
        {
          translations: {
            en: {
              name: "Height",
              description: "Height of the object"
            }
          },
          value: values?.dimension_values?.height || '0'
        },
        {
          translations: {
            en: {
              name: "Weight",
              description: "This is the kg"
            }
          },
          value: values?.dimension_values?.weight || '0'
        }
      ],
      translations: {
        en: {
          object_name: values?.translations?.en?.object_name,
          dyes: values?.translations?.en?.dyes,
          motifs: values?.translations?.en?.motifs,
          description: values?.translations?.en?.description,
          location: values?.translations?.en?.location,
          provenance: values?.translations?.en?.provenance,
          exhibition_history: values?.translations?.en?.exhibition_history,
          other_info: values?.translations?.en?.other_info,
        }
      },
    };

    if (id) {
      const finishUpdate = (message) => {
        navigate(`/management/catalogue-artefacts/${id}`);
        dispatch({type: 'STATE', payload: {change: true}});
        dispatch({
          type: SNACKBAR_OPEN,
          payload: {isNotify: true, severity: 'success', message}
        });
      };

      const updateCatalogue = () => catalogueAction('put', id, {...artefactsValues}).then(res => {
        finishUpdate('Artifacts updated successfully');
      });

      if (originalState === 'approved') {
        // Only open the catalogue for editing (approved -> in_progress) once the user
        // actually submits changes, not just for opening this form.
        catalogueTransition('put', id, {action: 'initiate_edit'}).then(() => {
          updateCatalogue();
        }).catch(err => {
          dispatch({
            type: SNACKBAR_OPEN,
            payload: {
              isNotify: true,
              severity: 'error',
              message: 'Unable to open this catalogue for editing. It may have changed state since you opened this form.'
            }
          });
        });
      } else {
        updateCatalogue();
      }
    } else {
      catalogueApi('post', {...artefactsValues}).then(res => {
        const createdId = res?.id ?? res?.data?.id;
        if (createdId) {
          navigate(`/management/catalogue-artefacts/${createdId}`);
        } else {
          navigate(`/management/catalogue-artefacts`);
        }
        dispatch({type: 'STATE', payload: {change: true}});
        dispatch({
          type: SNACKBAR_OPEN,
          payload: {isNotify: true, severity: 'success', message: 'Artifacts submitted successfully'}
        });
      })
    }
    setSubmitting(false);
  }

  const handleAction = () => {
    navigate(-1);
  }

  return (
    <PageWrapper>
      <Typography sx={{marginTop: 6}} className={'museum-font'}>
        {isEdit ? 'Edit Artefact' : 'Add Artefact'}
      </Typography>
      <Box className={'header-divider'}/>
      <Formik initialValues={initialValues}
              onSubmit={onSubmit} validationSchema={catalogueValidation}
              validateOnBlur={true} enableReinitialize
      >
        {({isSubmitting, touched, errors, values, setFieldValue, ...formik}) => (
          <Form>
            <Box sx={{mb: {sm: 4, xs: 2}}}>
              <Grid container spacing={2}>
                <Grid item sm={4} xs={12}>
                  <CatalogueEntryOne code={code} masters={masters}/>
                </Grid>
                <Grid item sm={4} xs={12}>
                  <CatalogueEntryTwo code={code} isEdit={isEdit} masters={masters}/>
                </Grid>
                <Grid item sm={4} xs={12}>
                  <CatalogueEntryThree code={code} masters={masters}/>
                </Grid>
              </Grid>
              <Box className={'dotted-divider'}/>
              <CatalogueAttachments setArtefactImgIds={setArtefactImgIds} imgId={id} artefactImgIds={artefactImgIds}/>
            </Box>
            <Grid container spacing={2}>
              <Grid item sm={8} xs={6}/>
              <Grid item sm={2} xs={12}>
                <Button fullWidth onClick={handleAction} variant={'outlined'} color={'error'}
                        className={'text-capitalize'}>CANCEL</Button>
              </Grid>
              <Grid item sm={2} xs={12}>
                <Button fullWidth variant={'contained'} type={'submit'}
                        className={'text-capitalize'}>{isEdit ? 'UPDATE' : 'SUBMIT'}</Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </PageWrapper>
  );
};